import { Col, Image, Row } from 'antd';
import { ReactNode, useLayoutEffect, useState } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { NorlysColor, NorlysFont } from '~src/branding';
import { boxShadows } from '~src/branding/boxShadow';
import { DisplayContext, useDisplayContext } from '~src/context/DisplayContext';
import { LEAD_ORIGIN_NORLYS } from '~src/hooks/useLeadOrigin';

import { Heading, Text } from '../Typography';

const Container = styled.div({
  maxWidth: '1600px',
  width: '100%',
  height: 'fit-content',
});

const TextContainer = styled.div({ overflow: 'hidden' });

const ImageAndTextWrapper = styled.div({ position: 'relative', height: 'fit-content' });

type ContentProps = Partial<DisplayContext>;

const BodilContent = ({ isMobile }: ContentProps) => {
  const containerStyle = {
    padding: isMobile ? '24px' : '56px 48px 48px 48px',
    backgroundColor: 'white',
  };

  const bodyTextStyle = { marginTop: '16px' };

  return (
    <TextContainer style={containerStyle}>
      <Heading>Vil du energioptimere din bolig?</Heading>
      <Heading level={2} style={{ marginTop: '24px', fontWeight: 500 }}>
        Prøv energiberegneren og se, hvor meget du kan spare.
      </Heading>
      <Text level={2} style={bodyTextStyle}>
        Hos Norlys vil vi gerne bidrage til den grønne omstilling. Derfor samarbejder vi med Bodil Energi, som er
        eksperter i at hjælpe dig med at energioptimere din bolig.
      </Text>
      <Text level={2} style={bodyTextStyle}>
        Lige nu får du 500 kr. rabat på din første elregning, hvis du skifter til Norlys i forbindelse med dit køb af
        varmepumpe hos Bodil Energi.
      </Text>
    </TextContainer>
  );
};

const NorlysContent = ({ isMobile, deviceType }: ContentProps) => {
  const isSmallMobile = deviceType === 'SMALL_MOBILE';

  const containerStyle = { padding: isMobile ? '24px' : '56px 48px 48px 48px' };

  const headingStyle = { ...NorlysFont.style('heading', isMobile ? (isSmallMobile ? 26 : 30) : 59) };
  const subHeadingStyle = {
    ...NorlysFont.style('default', isMobile ? (isSmallMobile ? 16 : 18) : 22),
    marginTop: isSmallMobile ? '16px' : '24px',
    fontWeight: 500,
  };
  const bodyTextStyle = {
    ...NorlysFont.style('default', isMobile ? (isSmallMobile ? 14 : 16) : 18),
    marginTop: isSmallMobile ? '8px' : '16px',
  };

  return (
    <TextContainer style={containerStyle}>
      <div style={headingStyle}>Vil du energioptimere din bolig?</div>
      <div style={subHeadingStyle}>Prøv energiberegneren og se, hvor meget du kan spare.</div>
      <div style={bodyTextStyle}>
        Hos Norlys vil vi gerne bidrage til den grønne omstilling. Derfor samarbejder vi med Bodil Energi, som er
        eksperter i at hjælpe dig med at energioptimere din bolig.
      </div>
      <div style={bodyTextStyle}>
        Lige nu får du 500 kr. rabat på din første elregning, hvis du skifter til Norlys i forbindelse med dit køb af
        varmepumpe hos Bodil Energi.
      </div>
    </TextContainer>
  );
};

const IMAGE_PROPS = {
  preview: false,
  width: '100%',
  height: '100%',
  style: { objectFit: 'cover', objectPosition: '25% 60%' } as CSSProperties,
};

type Props = {
  children?: ReactNode;
  imageURL?: string;
};

export const InfoBoxWithImage = (props: Props) => {
  const { leadOrigin } = useDisplayContext();

  return leadOrigin === LEAD_ORIGIN_NORLYS ? (
    <InfoBoxWithImageNorlys {...props} />
  ) : (
    <InfoBoxWithImageBodil {...props} />
  );
};

type BaseProps = Props & {
  containerStyle?: CSSProperties;
};

const InfoBoxWithImageBase = ({ children, imageURL, containerStyle }: BaseProps) => {
  const [height, setHeight] = useState<number>();
  const { isMobile, deviceType } = useDisplayContext();

  const imageHeight = document.querySelector('#addressPageImage')?.scrollHeight ?? 0;
  const containerHeight = document.querySelector('#addressPageTextContainer')?.scrollHeight ?? 0;

  useLayoutEffect(
    function setContentHeight() {
      const targetHeight = imageHeight / 2 + containerHeight;

      if (imageHeight > targetHeight) {
        setHeight(imageHeight);
        return;
      }

      setHeight(imageHeight / 2 + containerHeight);
    },
    [imageHeight, containerHeight]
  );

  if (isMobile) {
    const boxMargin = deviceType === 'SMALL_MOBILE' ? '16px' : '24px';

    return (
      <div style={{ height, marginTop: '16px' }}>
        <ImageAndTextWrapper>
          <Image id='addressPageImage' {...IMAGE_PROPS} src={imageURL} />
          <Container
            id='addressPageTextContainer'
            style={{
              position: 'absolute',
              top: '50%',
              left: boxMargin,
              width: `calc(100% - ${boxMargin} - ${boxMargin})`,
              ...containerStyle,
            }}
          >
            {children}
          </Container>
        </ImageAndTextWrapper>
      </div>
    );
  }

  return (
    <Row>
      <Col span={3} />
      <Col span={18}>
        <Container style={containerStyle}>
          <Row>
            <Col span={15}>{children}</Col>
            <Col span={9}>
              <Image {...IMAGE_PROPS} src={imageURL} />
            </Col>
          </Row>
        </Container>
      </Col>
      <Col span={3} />
    </Row>
  );
};

const InfoBoxWithImageBodil = ({ children, ...rest }: Props) => {
  const displayContext = useDisplayContext();

  return <InfoBoxWithImageBase {...rest}>{children ?? <BodilContent {...displayContext} />}</InfoBoxWithImageBase>;
};

const InfoBoxWithImageNorlys = ({ children, ...rest }: Props) => {
  const displayContext = useDisplayContext();

  return (
    <InfoBoxWithImageBase
      {...rest}
      {...{ containerStyle: { backgroundColor: NorlysColor.secondary.mint, ...boxShadows.norlys } }}
    >
      {children ?? <NorlysContent {...displayContext} />}
    </InfoBoxWithImageBase>
  );
};
